/**
 * roleUtils.ts — Centralized RBAC role helpers for the MRAC Command Center.
 *
 * Source of truth: RBAC Access Matrix (rbac_matrix.md)
 * Derived from: BRD v1.4 §5.1, FRD v2.0 §3 & Epic M (FR-M01 / US-M01),
 * FRD Appendix E ("Create User Groups").
 *
 * USAGE RULE: Never inline role checks across components. Import from here.
 */

// ── Role group constants ───────────────────────────────────────────────────────

/** All five clinical reviewer roles (nurses). */
export const REVIEWER_ROLES = [
  'reviewer_l1',
  'reviewer_l2',
  'reviewer_l3',
  'reviewer_qa',
  'reviewer_sme',
] as const

/** Roles with assignment/queue management authority. */
export const MANAGER_ROLES = ['admin', 'mrm'] as const

/**
 * Roles with claim assignment authority.
 * MRM, QAM, PC, and Program Manager have assignment governance authority.
 */
export const ASSIGNMENT_ROLES = [
  'admin',
  'mrm',
  'qam',
  'pc',
  'program_manager',
  'MR-Manager',
  'QA-Manager',
  'Project-Coordinator',
  'Program-Manager',
] as const

/**
 * Returns true if the MRM Assignment tab should come first before Reviewer Queue
 * in the Review Workbench (for MRM, QAM, Program Manager, and Project Coordinator).
 */
export function shouldSwapWorkbenchTabs(roles: string[]): boolean {
  return hasAnyRole(roles, [
    'mrm',
    'MR-Manager',
    'qam',
    'QA-Manager',
    'program_manager',
    'Program-Manager',
    'pc',
    'Project-Coordinator',
  ])
}

/**
 * D58: the MRM and the QAM work separate assignment lists. The QAM gets the
 * QA-listed claims, the MRM everything else, and L3 is on both; the backend
 * does the filtering (app/services/assignment_scope.py). True for a QAM who
 * holds no whole-queue role, so the workbench can label the list as theirs.
 */
export function isQamAssignmentListOnly(roles: string[]): boolean {
  return (
    hasAnyRole(roles, ['qam', 'QA-Manager']) &&
    !hasAnyRole(roles, ['mrm', 'MR-Manager', 'admin', 'program_manager', 'Program-Manager'])
  )
}

/**
 * Roles that have operational-level access (dashboards, reports, samples, etc.).
 * Pure reviewers are NOT in this list.
 */
export const OPERATIONAL_ROLES = [
  'admin',
  'mrm',
  'pc',
  'qam',
  'compliance',
  'compliance_director',
  'program_manager',
] as const

// ── Type helpers ───────────────────────────────────────────────────────────────

export type ReviewerRole = (typeof REVIEWER_ROLES)[number]
export type ManagerRole = (typeof MANAGER_ROLES)[number]
export type OperationalRole = (typeof OPERATIONAL_ROLES)[number]

// ── Role predicate functions ───────────────────────────────────────────────────

/**
 * Returns true if the user holds ANY reviewer role but NO operational role.
 *
 * Pure reviewers see a scoped personal dashboard, no org-wide metrics,
 * and no operational sidebar links. (FRD FR-M01 / US-M01 / FR-J06)
 */
export function isPureReviewer(roles: string[]): boolean {
  const hasReviewerRole = REVIEWER_ROLES.some((r) => roles.includes(r))
  const hasOperationalRole = OPERATIONAL_ROLES.some((r) => roles.includes(r))
  return hasReviewerRole && !hasOperationalRole
}

/**
 * Returns true if the user holds admin or mrm — the two roles with
 * assignment/queue management authority. (FRD FR-E01 / FR-E03)
 */
export function isManager(roles: string[]): boolean {
  return MANAGER_ROLES.some((r) => roles.includes(r))
}

/**
 * Returns true if the user's Workbench is the escalation-only view — today the
 * Compliance Director (CMD).
 *
 * "CMD Group" is a selectable escalation target (ESCALATION_ROLE_MAP in
 * app/models/escalation.py), so a CMD is handed clinical questions to answer,
 * and FRD US-G01 AC3 requires the targeted expert to see them in a queue. But a
 * CMD is not a reviewer level: it has no claim queue, no RFIs, no on-hold list,
 * and usually no row in `reviewers` at all — so the reviewer Workbench's other
 * sub-tabs have nothing to show it and its reviewer-profile lookup 404s.
 * Client UAT, Defect 31: "CMD should also have a Workbench page, like others on
 * the review team."
 *
 * Deliberately narrow. A user who ALSO holds a reviewer level or assignment
 * authority keeps the full Workbench for that role — this is only for an
 * account whose sole workbench-relevant hat is CMD.
 */
export function isEscalationOnlyWorkbench(roles: string[]): boolean {
  const isCmd = hasAnyRole(roles, ['compliance', 'compliance_director'])
  const hasReviewerRole = REVIEWER_ROLES.some((r) => roles.includes(r))
  return isCmd && !hasReviewerRole && !canAccessMrmAssignment(roles)
}

/**
 * Returns true if the user holds any of the given required roles.
 * Used for sidebar visibility and page-level route guards.
 */
export function hasAnyRole(roles: string[], required: readonly string[]): boolean {
  return required.some((r) => roles.includes(r))
}

/**
 * Returns true if the user holds admin role.
 * Used for Admin-only features (user mgmt, status overrides, etc.).
 */
export function isAdmin(roles: string[]): boolean {
  return roles.includes('admin')
}

/**
 * The single master user permitted to hard-delete Universes, Samples, and
 * Claims (cascading). This is an identity gate, NOT a role — it is enforced
 * canonically by the backend via authz.map.json (DELETE rules keyed on
 * {user.email}); this helper only governs UI visibility of the delete controls.
 *
 * Must stay in sync with the email literal in app/authz.map.json.
 */
export const SUPER_DELETER_EMAIL = 'masteruser@reli.grp'

/**
 * Returns true if the given email belongs to the master delete user.
 * Case-insensitive on the client for a forgiving UI; the backend comparison
 * is exact, so the token's email must still match the literal exactly.
 */
export function isSuperDeleter(email?: string | null): boolean {
  return !!email && email.toLowerCase() === SUPER_DELETER_EMAIL
}

/**
 * Returns true if user can view the Audit Trail on claims and samples.
 * Supervisors (admin, mrm, qam, compliance director) need full audit access.
 * (FRD FR-M02 / US-M02)
 */
export function canViewAuditTrail(roles: string[]): boolean {
  return hasAnyRole(roles, [
    'admin',
    'mrm',
    'qam',
    'compliance',
    'compliance_director',
    'program_manager',
  ])
}

/**
 * Sample submission (D36). Mirrors the route rules in app/authz.map.json:
 * the Submissions queue and a sample's Submission tab are visible to the
 * submission roles; the POC list, encryption log and Submission Rules are
 * maintained by MRAC's administrative team (MRM, PC, PM). The prepare/send
 * split inside the tab comes from the API's `permissions`, not from here.
 */
export const SAMPLE_SUBMISSION_VIEW_ROLES = ['admin', 'mrm', 'pc', 'qam', 'program_manager'] as const
export const SAMPLE_SUBMISSION_ADMIN_ROLES = ['admin', 'mrm', 'pc', 'program_manager'] as const

export function canViewSampleSubmissions(roles: string[]): boolean {
  return hasAnyRole(roles, SAMPLE_SUBMISSION_VIEW_ROLES)
}

export function canManageSubmissionSetup(roles: string[]): boolean {
  return hasAnyRole(roles, SAMPLE_SUBMISSION_ADMIN_ROLES)
}

/** Who approves and sends a sample submission, and so signs it (app/authz.map.json). */
export const SAMPLE_SUBMISSION_SIGNER_ROLES = ['admin', 'mrm', 'program_manager'] as const

export function canSignSampleSubmissions(roles: string[]): boolean {
  return hasAnyRole(roles, SAMPLE_SUBMISSION_SIGNER_ROLES)
}

/**
 * Every recognised spelling of a reviewer role — the internal snake_case slugs
 * AND their Keycloak display names from `MRAC_ROLES_REGISTRY` (app/core/roles.py).
 *
 * `session.roles` normally carries both spellings: the NextAuth callback keeps
 * the raw Keycloak role and appends its alias via the generated `KC_TO_AUTHZ`
 * map. Both are listed anyway, because this constant backs a DENY rule for
 * blind review — if that generator ever drifts and only the display name
 * survives, a slug-only check would quietly stop matching and the gate would
 * fail OPEN while still looking correct.
 */
export const REVIEWER_ROLE_TOKENS = [
  'reviewer_l1',
  'reviewer_l2',
  'reviewer_l3',
  'reviewer_qa',
  'reviewer_sme',
  'L1-Reviewer',
  'L2-Reviewer',
  'L3-Reviewer',
  'QA-Reviewer',
  'SME-Panel',
] as const

/**
 * Returns true if the user holds a clinical reviewer role under ANY spelling,
 * regardless of what else they hold. Unlike `isPureReviewer`, an accompanying
 * manager role does NOT cancel this out — that is the entire point at the call
 * sites that use it as a deny rule.
 */
export function holdsAnyReviewerRole(roles: string[]): boolean {
  return hasAnyRole(roles, REVIEWER_ROLE_TOKENS)
}

/**
 * Returns true if the user may see the full review history (every level's
 * determination, rationale and references) on a claim's Review tab.
 *
 * TWO GATES, AND THE ORDER MATTERS.
 *
 * 1. DENY: anyone holding a reviewer role is refused, even with mrm / qam /
 *    admin alongside it. 2. ALLOW: the remaining oversight roles are permitted.
 *
 * The deny rule exists because the allow list alone compromised blind review.
 * A dual-role account (say `reviewer_l2` + `qam`) would have been handed L1 and
 * QA determinations for a claim it may itself be assigned to review — and the
 * backend would not have stopped it, because the L2 blind filter in
 * app/routers/reviews.py deliberately exempts admin / mrm / qam. Blind review
 * is a BRD compliance requirement (L2 must not see L1), so the safe answer when
 * a person is both reviewer and overseer is to withhold the panel; they keep
 * every other route to the record. Explicit user decision, 2026-08-26.
 *
 * NOTE this is the OPPOSITE resolution to `canViewQaListed`, where an oversight
 * role deliberately overrides an L1/L2 role. That is intentional: the QA-Listed
 * badge is metadata about selection, whereas this panel is the prior reviewers'
 * clinical reasoning — the exact thing a blind reviewer must not read.
 *
 * Kept as its own predicate rather than reusing `canViewAuditTrail` (whose
 * oversight list is otherwise identical) because the two answer different
 * questions — that one gates the compliance event ledger, this one gates
 * clinical review content — and widening either must not silently widen the
 * other.
 */
export function canViewFullReviewHistory(roles: string[]): boolean {
  if (holdsAnyReviewerRole(roles)) return false
  return hasAnyRole(roles, [
    'admin',
    'mrm',
    'qam',
    'compliance',
    'compliance_director',
    'program_manager',
  ])
}

/**
 * Returns true if the user can access the MRM Assignment tab in the workbench.
 * Admin, MRM, and QAM have assignment governance authority per client RBAC clarification (M1).
 * QAM can assign claims globally. (FRD FR-E01 / FR-E03 + client RBAC feedback)
 */
export function canAccessMrmAssignment(roles: string[]): boolean {
  return hasAnyRole(roles, ASSIGNMENT_ROLES)
}

/**
 * Returns true if the user can see org-wide operational dashboard tabs.
 * Pure reviewers are restricted to their personal "My Queue" tab. (FRD FR-J06)
 */
export function canViewOperationalDashboard(roles: string[]): boolean {
  return !isPureReviewer(roles)
}

/**
 * Roles granted the programme-wide dashboard panels in the client's Role-Based
 * Access Guide S02: operational summary, SLA health, claim pipeline/queue,
 * information request aging, contractor scorecards and bottleneck analysis.
 *
 * The Compliance Director is deliberately ABSENT. S02 withholds every one of
 * those rows from CMD and grants the role a single panel: fraud/waste/abuse and
 * harm-hold metrics. This list mirrors the GET allow-lists on
 * /api/dashboard/operational, /sla-health, /queue, /rfi-aging,
 * /contractors/scorecards and /bottlenecks in app/authz.map.json, so the UI only
 * asks for what the API will actually answer.
 */
export const PROGRAMME_DASHBOARD_ROLES = [
  'admin',
  'mrm',
  'qam',
  'pc',
  'program_manager',
] as const

/** Returns true if the user may see the programme-wide dashboard panels (S02). */
export function canViewProgrammeDashboard(roles: string[]): boolean {
  return hasAnyRole(roles, PROGRAMME_DASHBOARD_ROLES)
}

/**
 * Returns true if the programme-wide dashboard panels must be WITHHELD from the
 * user - today that means the Compliance Director.
 *
 * Pure reviewers are excluded because they are not withheld, they are scoped:
 * SLA health, queue and bottlenecks are re-fetched with their own reviewer_id
 * ("own work" in S02), so those panels stay on screen for them.
 *
 * Callers must both skip the fetches (they would 403) and drop the panels. A
 * denied call previously fell through the `|| 0` fallback and rendered as a
 * real-looking zero on every KPI card.
 */
export function isProgrammeDashboardWithheld(roles: string[]): boolean {
  return !canViewProgrammeDashboard(roles) && !isPureReviewer(roles)
}

/**
 * Returns true if the user can view FWA & harm hold compliance metrics.
 * Restricted to supervisors, QAM, and compliance roles. (FRD FR-F05 / BRD §7.10)
 * QAM added per client RBAC feedback (M3: QAM needs FWA & Harm Hold oversight).
 */
export function canViewFwaMetrics(roles: string[]): boolean {
  return hasAnyRole(roles, ['admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager'])
}

/**
 * Returns true if the user may resolve a harm hold (resume / close / refer to CMS).
 *
 * Per MRAC Detailed Flow §SME Panel Review:
 *   - SME Panel (reviewer_sme) — validates the harm finding; primary clinical authority.
 *   - MRM (mrm)               — Medical Review Manager; validates alongside SME Panel.
 *   - QAM (qam)               — QA Manager oversight of FWA & Harm Hold (client RBAC M3).
 *   - Compliance Director      — owns the out-of-band compliance response (FRD §3).
 *   - Admin                    — platform-level override authority.
 *
 * In sync with BOTH backend layers (reconciled): the authz rule
 *   app/authz.map.json  /api/claims/{claim_id}/resolve-harm-hold  (ANY)
 * and the endpoint guard allowed_roles in
 *   app/routers/claims.py  resolve_harm_hold
 * all list the same five roles.
 */
export function canResolveHarmHold(roles: string[]): boolean {
  return hasAnyRole(roles, [
    'reviewer_sme',
    'mrm',
    'qam',
    'compliance_director',
    'admin',
  ])
}

/**
 * Returns true if the user may change a sample's CMS due dates — the
 * "CMS requests an alternate due date" path (FRD US-G04 AC3).
 *
 * MUST stay in sync with app/routers/samples.py _CMS_DUE_DATE_EDITOR_ROLES.
 */
export function canEditCmsDueDate(roles: string[]): boolean {
  return hasAnyRole(roles, [
    'admin',
    'mrm',
    'pc',
    'program_manager',
    'MR-Manager',
    'Project-Coordinator',
    'Program-Manager',
  ])
}

/**
 * Returns true if the user can view the AI Policies screen.
 * Admin has full CRUD; MRM has read-only. (BRD §13.2)
 */
export function canViewAiPolicies(roles: string[]): boolean {
  return hasAnyRole(roles, ['admin', 'mrm', 'program_manager'])
}

/**
 * Returns true if the user may record the contractor's original determination
 * on an existing claim (D24 AC2).
 *
 * MUST stay in sync with both backend layers:
 *   - app/authz.map.json  "/api/claims/[^/]+/contractor-determination"  PATCH
 *   - app/routers/claims.py  _DETERMINATION_EDITOR_ROLES
 *
 * Every reviewer role is deliberately absent. This value is the baseline the
 * reviewer's own Agree/Disagree decision is derived from, so a reviewer who
 * could set it could choose their own review outcome.
 */
export function canEditContractorDetermination(roles: string[]): boolean {
  return hasAnyRole(roles, [
    'admin',
    'mrm',
    'qam',
    'program_manager',
    'MR-Manager',
    'QA-Manager',
    'Program-Manager',
  ])
}

/**
 * Badge colours per review level. Single source of truth — this was copied
 * into ReviewerPickerModal and SampleAssignmentModal, and the MRM queue now
 * needs it too (D24 AC1: the queue shows which level each claim awaits).
 */
export const LEVEL_COLORS: Readonly<Record<string, { bg: string; text: string }>> = {
  L1: { bg: 'bg-blue-100', text: 'text-blue-700' },
  L2: { bg: 'bg-violet-100', text: 'text-violet-700' },
  L3: { bg: 'bg-purple-100', text: 'text-purple-700' },
  QA: { bg: 'bg-amber-100', text: 'text-amber-700' },
  SME: { bg: 'bg-teal-100', text: 'text-teal-700' },
}

/** Review levels a claim can be waiting for assignment at, in workflow order. */
export const ASSIGNMENT_LEVELS = ['L1', 'L2', 'L3', 'QA', 'SME'] as const
export type AssignmentLevel = (typeof ASSIGNMENT_LEVELS)[number]

/**
 * Roles the "QA Listed" indicator is HIDDEN from — first-pass clinical
 * reviewers only (L1 and L2).
 *
 * The QA subset is selected randomly BEFORE assignment specifically so that
 * assignment decisions cannot bias QA selection (FRD FR-D04, US-F06 — "QA
 * selection integrity"). If the first-pass reviewer could tell which of their
 * claims are under QA, that independence is undermined. RBAC (FRD FR-M01) is
 * the mechanism to scope the indicator.
 *
 * Client UAT (tracker D18, round 2 clarification): "The QA Listed indicator
 * should be hidden only from L1 and L2 reviewers to preserve the independence
 * of the random QA selection. L3, QA, SME, and CMD should retain visibility of
 * all review levels, including the QA Listed indicator, as part of their review
 * and oversight responsibilities."
 *
 * So this is now a deny-list, not an allow-list: everyone sees the badge except
 * reviewer_l1 / reviewer_l2 — and even they see it if they additionally hold an
 * L3 / QA / SME / oversight role (see QA_LISTED_OVERRIDE_ROLES).
 */
export const QA_LISTED_HIDDEN_ROLES = ['reviewer_l1', 'reviewer_l2'] as const

/**
 * Roles that grant QA Listed visibility even when the user also holds an
 * L1/L2 reviewer role (multi-role accounts). L3, QA, SME, CMD
 * (compliance_director) and the operational/management roles all perform
 * oversight across review levels, so their visibility wins.
 */
export const QA_LISTED_OVERRIDE_ROLES = [
  'admin',
  'mrm',
  'qam',
  'pc',
  'compliance',
  'compliance_director',
  'program_manager',
  'MR-Manager',
  'QA-Manager',
  'Project-Coordinator',
  'Program-Manager',
  'reviewer_l3',
  'reviewer_qa',
  'reviewer_sme',
] as const

/**
 * Returns true if the user may see the "QA Listed" indicator on a claim.
 * Hidden from L1 and L2 reviewers only, so the first-pass review stays blind to
 * QA selection. L3, QA, SME, CMD and all management/oversight roles see it.
 */
export function canViewQaListed(roles: string[]): boolean {
  if (hasAnyRole(roles, QA_LISTED_OVERRIDE_ROLES)) return true
  return !hasAnyRole(roles, QA_LISTED_HIDDEN_ROLES)
}

// ── Sidebar visibility map ─────────────────────────────────────────────────────

/**
 * Maps each sidebar route to the roles that are allowed to see it.
 * Drives Sidebar.tsx filtering. (RBAC Matrix §2)
 *
 * NOTE: Admin-only routes (/admin/*) use the existing `adminOnly` flag in
 * navItems config and are not duplicated here.
 */
export const SIDEBAR_VISIBILITY: Readonly<Record<string, readonly string[]>> = {
  '/': [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  // compliance_director (CMD) is included deliberately (Defect 31). "CMD Group"
  // is a selectable escalation target, and FRD US-G01 AC3 requires that the
  // targeted expert sees the claim "in their escalation workload immediately".
  // The CMD is not a reviewer level, so it gets the escalation-only Workbench —
  // see isEscalationOnlyWorkbench below.
  '/workbench': [
    'admin', 'mrm', 'qam', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  '/universes': ['admin', 'mrm', 'pc', 'compliance', 'compliance_director', 'program_manager'],
  '/samples': ['admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager'],
  '/rfis': ['admin', 'mrm', 'pc', 'qam', 'program_manager'],
  // D36: sample-level submission queue (completion emails and DA packages).
  '/submissions': ['admin', 'mrm', 'pc', 'qam', 'program_manager'],
  // Reviewer levels are included deliberately (Defect 34). The client's DA
  // process makes the first disagreeing reviewer (L1) the AUTHOR of the DA
  // Worksheet and one of its two required approvers, so they must be able to
  // reach this page. Without them here the page was hidden from the very person
  // step 4 of the process assigns the work to, even though the backend already
  // granted them the route and scoped the list to their own claims.
  //
  // Scoping is enforced server-side: GET /api/disagreements/worksheets narrows a
  // "pure" reviewer (a reviewer level with no manager role) to worksheets for
  // claims they personally reviewed, and every read endpoint re-checks
  // participation via reviewer_may_view_claim_worksheet. Showing the nav item
  // does not widen what any reviewer can see.
  '/disagreements': [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  '/reports': [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  '/clinical': [
    'admin', 'mrm', 'qam', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  '/ai/policies': ['admin', 'mrm', 'program_manager'],
  '/settings': [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
}

// ── Claim Detail tab visibility ────────────────────────────────────────────────

/**
 * Roles that can view each claim detail tab. (RBAC Matrix §5)
 *
 * The 'review' tab is visible to all roles with claim access.
 * The 'history' (Audit Trail) tab is supervisor-only.
 */
export const CLAIM_TAB_VISIBILITY: Readonly<Record<string, readonly string[]>> = {
  info: [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  lines: [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  review: [
    'admin', 'mrm', 'pc', 'qam', 'compliance', 'compliance_director', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  escalations: [
    'admin', 'mrm', 'pc', 'qam', 'program_manager',
    'reviewer_l1', 'reviewer_l2', 'reviewer_l3', 'reviewer_qa', 'reviewer_sme',
  ],
  history: [
    'admin', 'mrm', 'qam', 'compliance', 'compliance_director', 'program_manager',
  ],
}
